import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Navbar } from '../../components/Navbar';
import { BackButton } from '../../components/BackButton';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { 
  ArrowLeft, 
  Plus, 
  Pencil, 
  Trash2, 
  Box,
  AlertTriangle,
  Search,
  Sparkles,
  Upload,
  Camera,
  ImageIcon,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { DropToggles, DropSection, DropSwitch } from '../../components/DropToggles';
import { AvailabilityControls } from '../../components/AvailabilityControls';
import { SoldOutDemand } from '../../components/SoldOutDemand';
import { DropSettingsDialog } from '../../components/DropSettingsDialog';
import { DropPushPreview } from '../../components/DropPushPreview';
import { VARIANT_TEMPLATES, supportsVariants, suggestedVariantTemplate } from '../../utils/productVariants';
import { flowerSizesForProduct } from '../../utils/flowerSizes';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

// Feb 2026 — Phase 5.9.26 set `product.price = 0` for flower (the ladder is
// the source of truth). The legacy SA inventory list rendered $0.00 in that
// case, which was confusing. Use this helper to surface the lowest-tier price
// from the active region's ladder, falling back to the cross-region min, then
// to `product.price` for non-ladder categories. The optional `region` arg
// preserves the per-region tab context (DMV vs NJ).
function displayPriceForListing(product, region) {
  if (product?.category === 'flower') {
    const sizes = flowerSizesForProduct(product, region);
    const min = sizes.length > 0 ? Math.min(...sizes.map((s) => s.price)) : 0;
    return { value: min, isFrom: true };
  }
  return { value: Number(product?.price ?? 0), isFrom: false };
}

export function InventoryPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [products, setProducts] = useState([]);
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState('dmv');

  // Regional dispatcher → snap region to their warehouse once user data loads.
  // Super-admin keeps whatever they last picked (defaults to DMV).
  useEffect(() => {
    if (user && !isSuperAdmin && user.region && user.region !== region) {
      setRegion(user.region);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isSuperAdmin]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);

  // Feb 2026 — Product deep-link polish. When the ⌘K palette (or any other
  // caller) pushes `?productId=<id>` at us, we (a) apply a temporary highlight
  // to that row via `highlightedId`, (b) smooth-scroll it into view, then
  // (c) strip the param from the URL so a soft rerender doesn't re-trigger.
  // Highlight auto-fades after 2s.
  //
  // `firedForRef` guards against the effect re-firing when `setSearchParams`
  // itself triggers a re-render — without the guard, the effect's cleanup
  // would cancel the very scroll/highlight timers it just scheduled.
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightedId, setHighlightedId] = useState(null);
  const rowRefs = useRef({});
  const firedForRef = useRef(null);
  // Store the highlight-clear timer on a ref so it survives effect re-runs
  // that would otherwise clear it via the effect's cleanup.
  const clearTimerRef = useRef(null);

  useEffect(() => {
    const targetId = searchParams.get('productId');
    if (!targetId) return;
    if (loading || products.length === 0) return;
    if (firedForRef.current === targetId) return; // already handled
    const exists = products.some((p) => p.id === targetId);
    if (!exists) return;

    firedForRef.current = targetId;
    setHighlightedId(targetId);

    // Kick off scroll on the next paint so the ref has attached. Not wrapped
    // in the effect's cleanup — this rAF fires exactly once regardless of
    // re-renders triggered by our subsequent setSearchParams call.
    requestAnimationFrame(() => {
      const el = rowRefs.current[targetId];
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });

    // Auto-fade the highlight after the animation window. Timer kept on a
    // ref so nothing cancels it out from under us.
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    clearTimerRef.current = setTimeout(() => {
      setHighlightedId(null);
      clearTimerRef.current = null;
    }, 2200);

    // Strip the URL param — do it AFTER the current render commits so the
    // scroll + highlight above win the render race.
    setTimeout(() => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('productId');
        return next;
      }, { replace: true });
    }, 250);
  }, [searchParams, setSearchParams, loading, products]);

  // Cleanup on unmount only — never on effect re-run.
  useEffect(() => () => {
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
  }, []);

  // Feb 2026 — React Query (Phase 2). Uses a distinct key from the customer
  // products list (`['customer', 'products']`) because this endpoint returns
  // full admin-view rows (region_prices, variant_stock, etc.) while the
  // customer endpoint is stamped/filtered.
  const productsQuery = useQuery({
    queryKey: ['dispatch', 'products'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/products`, { withCredentials: true });
      return data || [];
    },
  });
  const regionsQuery = useQuery({
    queryKey: ['dispatch', 'regions'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_URL}/api/regions`, { withCredentials: true });
      const list = Array.isArray(data) ? data : (data.regions || []);
      return list.length ? list : [{ code: 'dmv', name: 'DMV' }, { code: 'nj', name: 'NJ' }];
    },
    // Regions rarely change — keep them cached for the full session.
    staleTime: 5 * 60_000,
  });

  // Shim so existing call sites (204, 216, 285 — post-save/delete refresh)
  // keep working without a wider rewrite. Also mirrors data into local state
  // so the rest of the file (search, filters, edit dialog) can keep reading
  // from `products` and `regions` without touching every reference.
  const fetchProducts = useCallback(async () => {
    await productsQuery.refetch();
  }, [productsQuery]);
  const fetchRegions = useCallback(async () => {
    await regionsQuery.refetch();
  }, [regionsQuery]);

  useEffect(() => {
    if (productsQuery.data) setProducts(productsQuery.data);
  }, [productsQuery.data]);
  useEffect(() => {
    if (regionsQuery.data) setRegions(regionsQuery.data);
  }, [regionsQuery.data]);
  useEffect(() => {
    // Only show the initial-load spinner on true cold-start; back-nav paints
    // instantly from cache while the background refetch runs.
    if (!productsQuery.isLoading && loading) setLoading(false);
  }, [productsQuery.isLoading, loading]);

  /**
   * Stock for the active region.
   * Returns `null` when this product isn't sold in this region —
   * the caller renders "N/A" / disables the input instead of
   * falsely showing the legacy aggregate `product.stock`.
   *
   * A product is "available in region X" if either:
   *   - `product.regions` is missing/empty (available everywhere), OR
   *   - `product.regions` includes X.
   */
  const isAvailableInRegion = (product, regionCode) => {
    const regs = product?.regions;
    if (!regs || !Array.isArray(regs) || regs.length === 0) return true;
    return regs.includes(regionCode);
  };

  const stockForRegion = (product, regionCode) => {
    if (!isAvailableInRegion(product, regionCode)) return null;
    if (product?.region_stock && Object.prototype.hasOwnProperty.call(product.region_stock, regionCode)) {
      return product.region_stock[regionCode] ?? 0;
    }
    // Product IS available in this region but has no region_stock entry yet
    // (e.g. seeded before region splitting). Default to 0, NOT the legacy
    // aggregate — that aggregate is shared across regions and would mis-imply
    // sellable units here.
    return 0;
  };

  const handleUpdateStock = async (productId, newStock) => {
    try {
      await axios.patch(
        `${API_URL}/api/products/${productId}/inventory`,
        { stock: parseInt(newStock, 10), region },
        { withCredentials: true },
      );
      toast.success(`Stock updated · ${region.toUpperCase()}`);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update stock');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/api/products/${productId}`, { withCredentials: true });
      toast.success('Product deleted');
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete product');
    }
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          p.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
    const matchesRegion = isAvailableInRegion(p, region);
    return matchesSearch && matchesCategory && matchesRegion;
  });

  // Count of products hidden purely because they aren't sold in the active region
  // (search + category still applied). Surfaced as a small hint so SA isn't
  // confused why the catalog count differs between region tabs.
  const hiddenByRegionCount = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          p.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
    return matchesSearch && matchesCategory && !isAvailableInRegion(p, region);
  }).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050003]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-[#39FF14] font-mono">
            <span className="cursor-blink">█</span> LOADING_INVENTORY...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050003] dispatch-safe-top" data-testid="inventory-page">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <BackButton to="/dispatch" className="shrink-0" />
            <h1 className="text-[#39FF14] font-mono text-lg sm:text-2xl font-bold neon-text truncate">
              {'>'} INVENTORY
            </h1>
          </div>

          <div className="flex items-center gap-2 flex-wrap justify-end">
            <DropSettingsDialog regions={isSuperAdmin ? regions.map((r) => r.code) : [user?.region].filter(Boolean)} />
            <Button
              type="button"
              variant="outline"
              data-testid="bulk-generate-images-btn"
              onClick={async () => {
                if (!window.confirm('Generate AI photos for every product missing one? This may take 30-60s and uses AI credits.')) return;
                const t = toast.loading('Generating missing images...');
                try {
                  const { data } = await axios.post(
                    `${API_URL}/api/products/generate-missing-images`,
                    {},
                    { withCredentials: true },
                  );
                  toast.success(
                    `${data.generated} generated · ${data.failed} failed (of ${data.total})`,
                    { id: t }
                  );
                  fetchProducts();
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Bulk generation failed', { id: t });
                }
              }}
              className="border-[#FF3D8E] text-[#FF3D8E] hover:bg-[rgba(255,61,142,0.08)] rounded-none font-mono"
            >
              <Sparkles className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">FIX MISSING PHOTOS</span>
            </Button>

            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] rounded-none" data-testid="add-product-btn">
                  <Plus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">ADD PRODUCT</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0C0612] border border-[#39FF14] rounded-none max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-[#39FF14] font-mono">ADD NEW PRODUCT</DialogTitle>
                </DialogHeader>
                <ProductForm onSuccess={() => { setShowAddDialog(false); fetchProducts(); }} onPhotoChange={fetchProducts} />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <SoldOutDemand region={region} refreshKey={products} onChanged={fetchProducts} />

        {/* Region indicator — toggle for super-admin (oversees all regions),
            read-only badge for regional dispatchers (locked to their warehouse). */}
        <div className="flex items-center gap-2 mb-4" data-testid="region-toggle">
          <span className="text-[#6B8E73] font-mono text-xs uppercase tracking-wider">// REGION</span>
          {isSuperAdmin ? (
            <div className="flex bg-[#0C0612] border border-[#2A0F3D] p-0.5">
              {regions.map((r) => {
                const active = region === r.code;
                return (
                  <button
                    key={r.code}
                    type="button"
                    onClick={() => setRegion(r.code)}
                    data-testid={`region-tab-${r.code}`}
                    className={`px-3 py-1.5 font-mono text-xs uppercase tracking-wider transition-colors ${
                      active ? 'bg-[#39FF14] text-[#050003]' : 'text-[#A3C7A8] hover:text-[#39FF14]'
                    }`}
                  >
                    {(r.name || r.code).toUpperCase()}
                  </button>
                );
              })}
            </div>
          ) : (
            <span
              className="px-3 py-1.5 bg-[rgba(57,255,20,0.08)] border border-[#39FF14] text-[#39FF14] font-mono text-xs uppercase tracking-wider"
              data-testid="region-locked"
            >
              {region.toUpperCase()}
            </span>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4B6B52]" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search products..."
              className="pl-10 bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono placeholder:text-[#4B6B52] focus:border-[#39FF14] rounded-none"
              data-testid="search-products"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full sm:w-48 bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none" data-testid="category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none">
              <SelectItem value="all" className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">ALL CATEGORIES</SelectItem>
              <SelectItem value="flower" className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">FLOWER</SelectItem>
              <SelectItem value="preroll" className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">PREROLL</SelectItem>
              <SelectItem value="edibles" className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">EDIBLES</SelectItem>
              <SelectItem value="concentrates" className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">CONCENTRATES</SelectItem>
              <SelectItem value="merch" className="text-[#39FF14] font-mono focus:bg-[#2A0F3D]">MERCH</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Hidden-by-region hint — keeps SA aware that the catalog count
            differs between region tabs because some SKUs aren't sold here. */}
        {hiddenByRegionCount > 0 && (
          <div
            className="mb-3 text-[#FF9F0A] font-mono text-xs uppercase tracking-wider px-3 py-2 border border-[#FF9F0A]/40 bg-[rgba(255,159,10,0.06)]"
            data-testid="inventory-hidden-by-region-hint"
          >
            // {hiddenByRegionCount} product{hiddenByRegionCount === 1 ? '' : 's'} hidden — not sold in {region.toUpperCase()}
          </div>
        )}

        {/* Products Table */}
        <div className="bg-[#0C0612] border border-[#2A0F3D]">
          {/* Desktop header — THC/CBD column removed; STOCK now shows the active region. */}
          <div className="hidden md:grid grid-cols-12 gap-4 p-4 border-b border-[#2A0F3D] text-[#6B8E73] font-mono text-xs uppercase">
            <div className="col-span-4">Product</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">Price</div>
            <div className="col-span-2">Stock · {region.toUpperCase()}</div>
            <div className="col-span-2">Actions</div>
          </div>

          {filteredProducts.map(product => {
            const regionStock = stockForRegion(product, region);
            // Now that we hide products not in region, regionStock will always
            // be a number here. Keep the null guard cheap & defensive.
            const lowStock = regionStock !== null && regionStock < 10;
            const priceDisplay = displayPriceForListing(product, region);
            return (
            <div
              key={product.id}
              ref={(el) => {
                if (el) rowRefs.current[product.id] = el;
                else delete rowRefs.current[product.id];
              }}
              className={`flex flex-col gap-3 md:grid md:grid-cols-12 md:gap-4 md:items-center p-4 border-b border-[#2A0F3D] hover:bg-[#111A14] transition-colors ${lowStock ? 'bg-[rgba(255,59,48,0.05)]' : ''} ${highlightedId === product.id ? 'product-row-highlight' : ''}`}
              data-testid={`product-row-${product.id}`}
            >
              {/* Row 1 on mobile: product name + price (top right) */}
              <div className="md:col-span-4 flex md:block items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[#39FF14] font-mono text-sm font-bold truncate">{product.name}</p>
                  <p className="text-[#4B6B52] font-mono text-xs line-clamp-1">{product.description}</p>
                  <AvailabilityControls product={product} region={region} onChange={fetchProducts} />
                </div>
                {/* Mobile: float price up here */}
                <p className="md:hidden text-[#39FF14] font-mono font-bold whitespace-nowrap" data-testid={`product-price-mobile-${product.id}`}>
                  {priceDisplay.isFrom ? 'from ' : ''}${Number(priceDisplay.value ?? 0).toFixed(2)}
                </p>
              </div>

              {/* Category + strain */}
              <div className="md:col-span-2 flex md:block items-center gap-2">
                <span className="text-[#A3C7A8] font-mono text-sm uppercase">{product.category}</span>
                {product.strain_type && (
                  <p className="text-[#6B8E73] font-mono text-xs md:mt-0">· {product.strain_type}</p>
                )}
              </div>

              {/* Desktop price cell */}
              <div className="hidden md:block md:col-span-2">
                <span className="text-[#39FF14] font-mono" data-testid={`product-price-desktop-${product.id}`}>
                  {priceDisplay.isFrom ? 'from ' : ''}${Number(priceDisplay.value ?? 0).toFixed(2)}
                </span>
              </div>

              {/* Stock input — labeled on mobile so the region is unambiguous */}
              <div className="md:col-span-2 flex items-center gap-2">
                {lowStock && <AlertTriangle className="w-4 h-4 text-[#FF3B30]" />}
                <span className="md:hidden text-[#6B8E73] font-mono text-xs uppercase">{region.toUpperCase()} stock</span>
                <Input
                  key={`${product.id}-${region}`}
                  type="number"
                  defaultValue={regionStock ?? 0}
                  min={0}
                  onBlur={(e) => {
                    if (parseInt(e.target.value, 10) !== regionStock) {
                      handleUpdateStock(product.id, e.target.value);
                    }
                  }}
                  className="w-24 h-8 bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none text-center"
                  data-testid={`stock-input-${product.id}`}
                />
              </div>

              {/* Actions */}
              <div className="md:col-span-2 flex gap-2">
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-[#2A0F3D] text-[#39FF14] hover:border-[#39FF14] rounded-none"
                      onClick={() => setEditingProduct(product)}
                      data-testid={`edit-product-${product.id}`}
                    >
                      <Pencil className="w-3 h-3" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#0C0612] border border-[#39FF14] rounded-none max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="text-[#39FF14] font-mono">EDIT PRODUCT</DialogTitle>
                    </DialogHeader>
                    <ProductForm product={editingProduct} onSuccess={() => { setEditingProduct(null); fetchProducts(); }} onPhotoChange={fetchProducts} />
                  </DialogContent>
                </Dialog>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDeleteProduct(product.id)}
                  className="border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)] rounded-none"
                  data-testid={`delete-product-${product.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
            );
          })}

          {filteredProducts.length === 0 && (
            <div className="p-12 text-center">
              <Box className="w-12 h-12 text-[#4B6B52] mx-auto mb-4" />
              <p className="text-[#4B6B52] font-mono">// NO PRODUCTS FOUND</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductForm({ product, onSuccess, onPhotoChange }) {
  const { user } = useAuth();
  const isSuper = user?.role === 'super_admin';
  // Phase 6.0 — `internalProduct` tracks the form's current "saved" identity.
  // Initialized from the `product` prop; promoted to the just-created product
  // after a successful CREATE so the button label flips from CREATE → UPDATE
  // and the next save hits the PUT path instead of POSTing a duplicate.
  const [internalProduct, setInternalProduct] = useState(product || null);

  const actorRegion = user?.region;
  const [pushPreview, setPushPreview] = useState(null);
  // Phase 5.9.5 — fetch the region list once for the "Available regions" picker.
  // Regional dispatchers don't see the picker but we still fetch for consistency
  // (the request is cheap and used elsewhere too).
  const [regions, setRegions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/regions`, { withCredentials: true })
      .then(({ data }) => {
        if (!cancelled) setRegions(Array.isArray(data) ? data : (data.regions || []));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  // Phase 5.9.4 — which regions can this form edit? Super admin edits all;
  // regional admin edits only their region.
  const editableRegions = isSuper
    ? (product?.regions || ['dmv', 'nj'])
    : (actorRegion ? [actorRegion] : []);

  const initialRegionPrices = (() => {
    const rp = product?.region_prices || {};
    const out = {};
    for (const r of editableRegions) {
      const f = (rp[r] || {}).flower_prices || {};
      out[r] = {
        '3.5g': f['3.5g'] ?? '',
        '7g':   f['7g']   ?? '',
        '14g':  f['14g']  ?? '',
        '28g':  f['28g']  ?? '',
      };
    }
    return out;
  })();

  // Feb 2026 — per-region variant prices for concentrate/preroll. Mirrors the
  // flower ladder architecture so regional dispatchers can independently
  // price non-flower variants (e.g. NJ prices vape 2g differently than DMV).
  // Shape: { <region>: { <variant_key>: '<price>' } }. Empty values stay
  // as '' and are omitted at save so the backend falls back to the shared
  // `variants[].price`. The pricing helper `_variant_price_for` already
  // prefers `region_prices.<region>.variant_prices.<key>` first.
  const initialRegionVariantPrices = (() => {
    const rp = product?.region_prices || {};
    const out = {};
    for (const r of editableRegions) {
      const vp = (rp[r] || {}).variant_prices || {};
      // Seed with every existing variant key from the product so the UI
      // always has an input row even before the operator fills anything in.
      const seed = {};
      for (const v of (product?.variants || [])) {
        const k = v.key;
        if (!k) continue;
        seed[k] = vp[k] ?? '';
      }
      // Also carry any legacy variant_prices key not in variants[] (defensive
      // — an operator may have deleted a variant that still has a stale price).
      for (const [k, val] of Object.entries(vp)) {
        if (!(k in seed)) seed[k] = val ?? '';
      }
      out[r] = seed;
    }
    return out;
  })();

  const [formData, setFormData] = useState({
    name: product?.name || '',
    category: product?.category || 'flower',
    subcategory: product?.subcategory || '',
    description: product?.description || '',
    price: product?.price || '',
    strain_type: product?.strain_type || '',
    stock: product?.stock || 0,
    image_url: product?.image_url || '',
    vibe_description: product?.vibe_description || '',
    // Phase 5.9.3 — global flower prices (super-admin-only legacy field).
    flower_prices: {
      '3.5g': product?.flower_prices?.['3.5g'] ?? '',
      '7g':   product?.flower_prices?.['7g']   ?? '',
      '14g':  product?.flower_prices?.['14g']  ?? '',
      '28g':  product?.flower_prices?.['28g']  ?? '',
    },
    // Phase 5.9.4 — per-region overrides.
    region_prices: initialRegionPrices,
    // Feb 2026 — per-region variant prices for concentrate/preroll. Populated
    // from `product.region_prices[<region>].variant_prices` and merged back
    // into the region_prices payload at save time. See panel below the
    // variants table.
    region_variant_prices: initialRegionVariantPrices,
    // Phase 5.9.5 — which regions this product is available in. Super-admin
    // can toggle DMV/NJ on a single product (dual-region inventory) via the
    // multi-select below. Regional dispatchers don't see this — their region
    // is auto-tagged server-side on create.
    regions: Array.isArray(product?.regions) && product.regions.length > 0
      ? product.regions
      : (actorRegion ? [actorRegion] : []),
    // Phase 2 — variants (apparel sizes + concentrate weights).
    // Shape: [{key, label, price?}], pulled from defaults or product.
    variants: Array.isArray(product?.variants) ? product.variants.map((v) => ({ ...v })) : [],
    // Phase 2 — variant stock matrix: { variant_key: { region: stock } }
    // We track these client-side as a flat map and POST each region's slice
    // via the existing PATCH /products/{id}/inventory endpoint.
    variant_stock: product?.variant_stock
      ? Object.fromEntries(Object.entries(product.variant_stock).map(([k, byRegion]) => [k, { ...byRegion }]))
      : {},
    _variant_stock_pending: {},   // { "<key>:<region>": newStock } — flushed via PATCH on save
    // Phase 6.0 Sub-Phase 2 — featured flag + lab data (cannabinoids + terpenes).
    // Both optional; customer drawer/featured-section hide gracefully when empty.
    featured: false,
    lab_data: {
      cannabinoids: { ...(product?.lab_data?.cannabinoids || {}) },
      terpenes: { ...(product?.lab_data?.terpenes || {}) },
    },
    // Feb 2026 — operator opt-out for AI auto-vibe on create. Persisted on
    // the product so SAVE-without-AI behavior survives subsequent edits.
    skip_ai_vibe: !!product?.skip_ai_vibe,
  });
  const [loading, setLoading] = useState(false);
  const [generatingVibe, setGeneratingVibe] = useState(false);
  const [vibeTone, setVibeTone] = useState('plain'); // Feb 2026 — default to PLAIN (literal, no metaphors). Pick "random" for surprise shuffle.
  const [generatingImage, setGeneratingImage] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  // Phase 6.0 Sub-Phase 2 — auto-fill loading states.
  const [autoFillingDesc, setAutoFillingDesc] = useState(false);
  const [autoFillingLab, setAutoFillingLab] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Phase 5.9.19 — block save when any variant key is malformed. Lets the
    // operator fix the typo before the request reaches the backend (which
    // would also reject with a 422 — this is just a kinder UX).
    if (isSuper && Array.isArray(formData.variants) && formData.variants.length > 0) {
      const badKeys = formData.variants
        .map((v, i) => ({ idx: i, key: v.key, error: validateVariantKey(v.key) }))
        .filter((r) => r.error);
      if (badKeys.length > 0) {
        const first = badKeys[0];
        toast.error(`Variant ${first.idx + 1}: ${first.error}`);
        return;
      }
      // Phase 5.9.26 — concentrate variants must each carry a positive price.
      if (isConcentrate) {
        const badPrices = formData.variants
          .map((v, i) => ({ idx: i, price: Number(v.price) }))
          .filter((r) => !Number.isFinite(r.price) || r.price <= 0);
        if (badPrices.length > 0) {
          toast.error(`Variant ${badPrices[0].idx + 1}: price required (each concentrate size is priced separately)`);
          return;
        }
      }
    }

    setLoading(true);

    // Phase 5.9.26 — flower hides the price field (ladder governs); concentrate
    // hides it too (variants govern). For hidden cases, send 0 / min variant price
    // so the backend Pydantic validator can finalize the value.
    let resolvedPrice = parseFloat(formData.price);
    if (isFlower) {
      resolvedPrice = 0;
    } else if (isConcentrate) {
      const variantPrices = (formData.variants || [])
        .map((v) => Number(v.price))
        .filter((p) => Number.isFinite(p) && p > 0);
      resolvedPrice = variantPrices.length > 0 ? Math.min(...variantPrices) : 0;
    }
    if (!Number.isFinite(resolvedPrice)) resolvedPrice = 0;

    const data = {
      ...formData,
      price: resolvedPrice,
      strain_type: formData.strain_type || null,
      stock: parseInt(formData.stock, 10),
      vibe_description: formData.vibe_description || null,
      subcategory: formData.subcategory || null,
      // Phase 2 — variants are super-admin-owned; regional dispatchers send
      // the existing list unchanged. Strip price field for apparel (shared base price).
      variants: isSuper
        ? (formData.variants && formData.variants.length > 0
            ? formData.variants
                .filter((v) => v && v.key && v.label)
                .map((v) => {
                  const p = v.price === '' || v.price === null || v.price === undefined
                    ? null
                    : Number(v.price);
                  const out = { key: String(v.key).trim(), label: String(v.label).trim() };
                  if (p !== null && !Number.isNaN(p) && p > 0) out.price = p;
                  return out;
                })
            : null)
        : (product?.variants ?? null),
      // Phase 2 — variant_stock: regional admins only send their own region's slice.
      // The backend safe-merges. We strip empty/non-numeric values for hygiene.
      variant_stock: (() => {
        const src = formData.variant_stock || {};
        const out = {};
        for (const [vk, byRegion] of Object.entries(src)) {
          const cleaned = {};
          for (const [r, n] of Object.entries(byRegion || {})) {
            if (n === '' || n === null || n === undefined) continue;
            const v = Number(n);
            if (Number.isNaN(v)) continue;
            if (!isSuper && r !== actorRegion) continue; // strip foreign-region slices
            cleaned[r] = Math.max(0, Math.floor(v));
          }
          if (Object.keys(cleaned).length) out[vk] = cleaned;
        }
        return Object.keys(out).length ? out : null;
      })(),
      // Phase 5.9.4 — legacy global flower_prices: super-admin can still set.
      // Empty strings → omitted so the backend falls back to the ladder.
      flower_prices: formData.category === 'flower' && isSuper
        ? (() => {
            const fp = Object.fromEntries(
              Object.entries(formData.flower_prices)
                .map(([k, v]) => [k, v === '' || v === null || v === undefined ? null : parseFloat(v)])
                .filter(([, v]) => v !== null && !Number.isNaN(v) && v > 0),
            );
            return Object.keys(fp).length ? fp : null;
          })()
        : (product?.flower_prices ?? null),
      // Phase 5.9.4 — per-region overrides. Only the regions this user can edit.
      // Feb 2026 — flower ships `flower_prices`; concentrate/preroll ships
      // `variant_prices` so regional dispatchers can price variants independently.
      region_prices: (() => {
        const isVariantPricedCategory =
          formData.category === 'concentrates' || formData.category === 'preroll';
        if (formData.category !== 'flower' && !isVariantPricedCategory) return null;
        const out = {};
        for (const r of editableRegions) {
          const slice = {};
          if (formData.category === 'flower') {
            const fp = Object.fromEntries(
              Object.entries(formData.region_prices[r] || {})
                .map(([k, v]) => [k, v === '' || v === null || v === undefined ? null : parseFloat(v)])
                .filter(([, v]) => v !== null && !Number.isNaN(v) && v > 0),
            );
            if (Object.keys(fp).length) slice.flower_prices = fp;
          }
          if (isVariantPricedCategory) {
            const vp = Object.fromEntries(
              Object.entries(formData.region_variant_prices[r] || {})
                .map(([k, v]) => [k, v === '' || v === null || v === undefined ? null : parseFloat(v)])
                .filter(([, v]) => v !== null && !Number.isNaN(v) && v > 0),
            );
            if (Object.keys(vp).length) slice.variant_prices = vp;
          }
          if (Object.keys(slice).length) out[r] = slice;
        }
        return Object.keys(out).length ? out : null;
      })(),
      // Phase 5.9.5 — `regions` is a shared field; super_admin owns it.
      // Regional dispatchers send the existing value unchanged so backend
      // safe-merge keeps it intact. Phase 7-lite: super_admin must explicitly
      // choose >=1 region on CREATE; we send what they picked (or [] which
      // the backend rejects with a clear error).
      regions: isSuper
        ? (formData.regions && formData.regions.length > 0 ? formData.regions : [])
        : (product?.regions ?? null),
      // Phase 6.0 Sub-Phase 2 — featured flag + lab_data. Super-admin owned.
      // Regional dispatchers send the existing value unchanged. Empty objects
      // are sent as `null` so the customer drawer hides the panels cleanly.
      featured: !!formData.featured,
      lab_data: isSuper
        ? (() => {
            const cb = formData.lab_data?.cannabinoids || {};
            const tp = formData.lab_data?.terpenes || {};
            const cleanedCb = {};
            for (const [k, v] of Object.entries(cb)) {
              const key = String(k || '').trim();
              if (!key) continue;
              const n = parseFloat(v);
              if (!Number.isNaN(n) && n >= 0) cleanedCb[key] = n;
            }
            const cleanedTp = {};
            for (const [k, v] of Object.entries(tp)) {
              const key = String(k || '').trim();
              if (!key) continue;
              const n = parseFloat(v);
              if (!Number.isNaN(n) && n >= 0) cleanedTp[key] = n;
            }
            const out = {};
            if (Object.keys(cleanedCb).length > 0) out.cannabinoids = cleanedCb;
            if (Object.keys(cleanedTp).length > 0) out.terpenes = cleanedTp;
            return Object.keys(out).length > 0 ? out : null;
          })()
        : (product?.lab_data ?? null),
    };

    // Phase 7-lite — pre-flight guard for super_admin creating a NEW product.
    // Backend would 400 anyway, but a friendlier client-side toast keeps the
    // form state intact instead of reaching the network round-trip.
    if (!product && isSuper && (!formData.regions || formData.regions.length === 0)) {
      toast.error('Pick at least one region (DMV / NJ) before saving — products no longer auto-publish everywhere.');
      return;
    }

    try {
      let savedProduct;
      let newDropRegions = [];
      if (internalProduct) {
        const res = await axios.put(`${API_URL}/api/products/${internalProduct.id}`, data, { withCredentials: true });
        savedProduct = res.data;
        toast.success('Product updated');
      } else {
        // On CREATE for flower, backend auto-generates vibe_description if not provided.
        const res = await axios.post(`${API_URL}/api/products`, data, { withCredentials: true });
        savedProduct = res.data;
        toast.success('Product created');
        // Phase 6.0 — promote internalProduct so subsequent saves are PUTs and
        // the button label flips to UPDATE PRODUCT.
        if (savedProduct?.id) setInternalProduct(savedProduct);
        newDropRegions = Object.keys(savedProduct?.region_drop || {});
      }
      // Phase 5.9.7 — per-region stock edits flow through the existing inventory
      // PATCH endpoint so we reuse the audit/ledger/SSE infra. Each region with
      // a pending number fires its own PATCH (cheap, region-scoped).
      const pending = formData._region_stock_pending || {};
      const pendingKeys = Object.keys(pending);
      if (pendingKeys.length > 0 && savedProduct?.id) {
        for (const region of pendingKeys) {
          const newCount = pending[region];
          if (newCount === null || Number.isNaN(newCount)) continue;
          try {
            await axios.patch(
              `${API_URL}/api/products/${savedProduct.id}/inventory`,
              { stock: newCount, region },
              { withCredentials: true },
            );
          } catch (err) {
            console.warn('region stock patch failed', region, err);
          }
        }
      }
      // Phase 2 — flush per-variant per-region stock changes via the same
      // PATCH endpoint (now accepts an optional variant_key).
      const pendingV = formData._variant_stock_pending || {};
      const pendingVKeys = Object.keys(pendingV);
      if (pendingVKeys.length > 0 && savedProduct?.id) {
        for (const composite of pendingVKeys) {
          const [vkey, region] = composite.split('::');
          const newCount = pendingV[composite];
          if (newCount === null || Number.isNaN(newCount)) continue;
          try {
            await axios.patch(
              `${API_URL}/api/products/${savedProduct.id}/inventory`,
              { stock: newCount, region, variant_key: vkey },
              { withCredentials: true },
            );
          } catch (err) {
            console.warn('variant stock patch failed', composite, err);
          }
        }
      }
      if (newDropRegions.length) setPushPreview({ productId: savedProduct.id, regions: newDropRegions });
      else onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  // Phase 5.7 — re-roll the LLM-generated vibe description for an EXISTING product.
  // Tone is REQUIRED in the body (Feb 2026): the backend now defaults missing
  // tone to "plain", so we must explicitly forward the operator's selection —
  // including "random" — for the surprise-shuffle path to fire.
  const handleRegenerateVibe = async () => {
    if (!internalProduct?.id) return;
    setGeneratingVibe(true);
    try {
      const body = vibeTone ? { tone: vibeTone } : {};
      const { data } = await axios.post(
        `${API_URL}/api/products/${internalProduct.id}/regenerate-vibe-description`,
        body,
        { withCredentials: true },
      );
      setFormData((prev) => ({ ...prev, vibe_description: data.vibe_description }));
      toast.success(`New ${data.tone === 'random' ? 'description' : data.tone + ' take'} generated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate description');
    } finally {
      setGeneratingVibe(false);
    }
  };

  // Phase 6.0 Sub-Phase 2 — auto-fill the long `description` field via LLM.
  // Works for both EDIT (uses saved product) and ADD NEW (uses the in-progress
  // form data via the /products/draft endpoint).
  const handleAutoFillDescription = async () => {
    if (!formData.name || !formData.name.trim()) {
      toast.error('Enter a product name first');
      return;
    }
    setAutoFillingDesc(true);
    try {
      const url = internalProduct?.id
        ? `${API_URL}/api/products/${internalProduct.id}/auto-fill-description`
        : `${API_URL}/api/products/draft/auto-fill-description`;
      const body = internalProduct?.id ? {} : {
        name: formData.name,
        category: formData.category,
        strain_type: formData.strain_type || null,
        description: formData.description || null,
        vibe_description: formData.vibe_description || null,
      };
      const { data } = await axios.post(url, body, { withCredentials: true });
      setFormData((prev) => ({ ...prev, description: data.description }));
      toast.success('Description drafted — edit before saving if needed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to draft description');
    } finally {
      setAutoFillingDesc(false);
    }
  };

  // Phase 6.0 Sub-Phase 2 — auto-fill cannabinoids + terpenes via LLM. Same
  // dual-mode behavior as description above.
  const handleAutoFillLabData = async () => {
    if (!formData.name || !formData.name.trim()) {
      toast.error('Enter a product name first');
      return;
    }
    setAutoFillingLab(true);
    try {
      const url = internalProduct?.id
        ? `${API_URL}/api/products/${internalProduct.id}/auto-fill-lab-data`
        : `${API_URL}/api/products/draft/auto-fill-lab-data`;
      const body = internalProduct?.id ? {} : {
        name: formData.name,
        strain_type: formData.strain_type || 'hybrid',
      };
      const { data } = await axios.post(url, body, { withCredentials: true });
      setFormData((prev) => ({
        ...prev,
        lab_data: {
          cannabinoids: { ...(data.lab_data?.cannabinoids || {}) },
          terpenes: { ...(data.lab_data?.terpenes || {}) },
        },
      }));
      toast.success('Lab data drafted — review + edit before saving');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to draft lab data');
    } finally {
      setAutoFillingLab(false);
    }
  };

  // Phase 6.0 Sub-Phase 2 — small helpers for the lab-data accordion editor.
  const updateLabValue = (kind, key, value) => {
    setFormData((prev) => ({
      ...prev,
      lab_data: {
        ...(prev.lab_data || {}),
        [kind]: { ...(prev.lab_data?.[kind] || {}), [key]: value },
      },
    }));
  };
  const removeLabEntry = (kind, key) => {
    setFormData((prev) => {
      const next = { ...(prev.lab_data?.[kind] || {}) };
      delete next[key];
      return { ...prev, lab_data: { ...(prev.lab_data || {}), [kind]: next } };
    });
  };
  const addLabEntry = (kind, key) => {
    const k = String(key || '').trim();
    if (!k) return;
    setFormData((prev) => ({
      ...prev,
      lab_data: {
        ...(prev.lab_data || {}),
        [kind]: { ...(prev.lab_data?.[kind] || {}), [k]: '' },
      },
    }));
  };

  // Phase 5.9 — AI product photography (Nano Banana) + dispatcher upload override.
  // Saves directly to the product on the backend; we reflect the new URL into
  // the form state so the preview updates and the eventual form submit doesn't
  // overwrite it with stale data.
  //
  // `useReference=true` posts the current product image as a reference so the
  // model RESTYLES it rather than generating from scratch. `customPrompt` lets
  // the dispatcher steer the look ("magenta accent light", "lifestyle shot", etc.).
  const handleGenerateImage = async ({ useReference = false } = {}) => {
    if (!internalProduct?.id) return;
    setGeneratingImage(true);
    try {
      const form = new FormData();
      const prompt = imagePrompt.trim();
      if (prompt) form.append('custom_prompt', prompt);
      if (useReference) form.append('use_current_image', 'true');
      const { data } = await axios.post(
        `${API_URL}/api/products/${internalProduct.id}/generate-image`,
        form,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setFormData((prev) => ({ ...prev, image_url: data.image_url }));
      toast.success(useReference ? 'Photo restyled with your direction' : 'New AI photo generated');
      onPhotoChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image generation failed');
    } finally {
      setGeneratingImage(false);
    }
  };

  // Phase 5.9.10 — Generate a brand-aligned photo using a user-supplied
  // reference image (vendor pic, real product on a counter, etc.). Solves the
  // "vape photo doesn't match our products" gap — `AI RESTYLE` only re-uses
  // the existing product image, this lets the dispatcher inject a fresh
  // reference of their choice. Backend already accepts `reference_file`
  // multipart on /generate-image, so this is pure UI/UX.
  const handleGenerateFromPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so picking the same file again still fires
    if (!file || !internalProduct?.id) return;
    if (file.type && !file.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Reference too large (max 8 MB)');
      return;
    }
    setGeneratingImage(true);
    try {
      const form = new FormData();
      const prompt = imagePrompt.trim();
      if (prompt) form.append('custom_prompt', prompt);
      form.append('reference_file', file, file.name || 'reference.png');
      const { data } = await axios.post(
        `${API_URL}/api/products/${internalProduct.id}/generate-image`,
        form,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setFormData((prev) => ({ ...prev, image_url: data.image_url }));
      toast.success('Photo generated using your reference');
      onPhotoChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so picking the same file again still fires
    if (!file || !internalProduct?.id) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    setUploadingImage(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(
        `${API_URL}/api/products/${internalProduct.id}/upload-image`,
        form,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setFormData((prev) => ({ ...prev, image_url: data.image_url }));
      toast.success('Photo uploaded');
      onPhotoChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  // Phase 5.9.10 hotfix — remove a bad photo entirely (no need to re-upload
  // a placeholder). Backend clears image_url + unlinks the file, then we
  // reset the form preview and ask the parent to refetch so every table row
  // showing this product also flips to the placeholder icon.
  const handleDeleteImage = async () => {
    if (!internalProduct?.id) return;
    if (!window.confirm('Remove this photo? You can always regenerate or upload another one.')) {
      return;
    }
    try {
      await axios.delete(
        `${API_URL}/api/products/${internalProduct.id}/image`,
        { withCredentials: true },
      );
      setFormData((prev) => ({ ...prev, image_url: '' }));
      toast.success('Photo removed');
      onPhotoChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove photo');
    }
  };

  // Resolve the preview URL: server-relative paths get prefixed with API_URL,
  // absolute URLs (e.g., legacy Unsplash links) are used as-is.
  const previewSrc = formData.image_url
    ? (formData.image_url.startsWith('http') ? formData.image_url : `${API_URL}${formData.image_url}`)
    : '';

  const isFlower = formData.category === 'flower';
  // Phase 5.9.26 — concentrate uses per-variant prices (0.5g/1g/2g each have
  // their own price); the top-level `price` input is redundant noise and now
  // hidden. Flower already hides via the price ladder below. Both categories
  // auto-populate `formData.price` at save time (flower → 0; concentrate →
  // min variant price) so the doc stays consistent for legacy fallbacks.
  const isConcentrate = formData.category === 'concentrates';
  const hidePriceField = isFlower || isConcentrate;
  const variantSupport = supportsVariants(formData.category, formData.subcategory);
  // Phase 6.0 — categories that need a per-variant price column in the editor
  // (in addition to key + label). Concentrates AND preroll both fit this.
  const wantsVariantPriceColumn =
    formData.category === 'concentrates' || formData.category === 'preroll';
  const hasAnyVariants = (formData.variants || []).length > 0;

  // Phase 2 — helpers for the variants editor.
  const seedVariantsFromTemplate = () => {
    const tplKey = suggestedVariantTemplate({
      category: formData.category,
      subcategory: formData.subcategory,
    });
    if (!tplKey) return;
    const template = VARIANT_TEMPLATES[tplKey] || [];
    setFormData((prev) => ({
      ...prev,
      variants: template.map((v) => ({ ...v })),
    }));
    toast.success(`Loaded ${template.length} default ${tplKey} variants`);
  };

  const updateVariantField = (idx, field, value) => {
    setFormData((prev) => {
      const next = [...(prev.variants || [])];
      next[idx] = { ...next[idx], [field]: value };
      return { ...prev, variants: next };
    });
  };

  // Phase 5.9.19 — inline validation for variant keys. Mirrors the backend
  // `_ensure_safe_mongo_segment` rules so the operator sees the same error
  // before the request even leaves the browser. Returns null on valid, else
  // a short user-facing reason string.
  const validateVariantKey = (key) => {
    if (key === undefined || key === null) return 'Variant key is required';
    if (typeof key !== 'string') return 'Variant key must be text';
    const v = key.trim();
    if (!v) return 'Variant key cannot be empty';
    if (v.startsWith('.') || v.endsWith('.')) {
      return "Can't start or end with a dot. Use '0.5g' instead of '.5g'.";
    }
    if (v.includes('..')) return 'Cannot contain two consecutive dots';
    if (v.includes('$') || v.includes('\0')) return 'Contains a reserved character ($, null)';
    if (v.length > 80) return 'Too long (max 80 characters)';
    return null;
  };

  const removeVariant = (idx) => {
    setFormData((prev) => {
      const removedKey = (prev.variants || [])[idx]?.key;
      const nextVariants = (prev.variants || []).filter((_, i) => i !== idx);
      const nextStock = { ...(prev.variant_stock || {}) };
      if (removedKey) delete nextStock[removedKey];
      return { ...prev, variants: nextVariants, variant_stock: nextStock };
    });
  };

  const addBlankVariant = () => {
    setFormData((prev) => ({
      ...prev,
      variants: [...(prev.variants || []), { key: '', label: '', price: '' }],
    }));
  };

  const clearVariants = () => {
    setFormData((prev) => ({ ...prev, variants: [], variant_stock: {} }));
  };

  const setVariantStock = (vkey, regionCode, value) => {
    setFormData((prev) => ({
      ...prev,
      variant_stock: {
        ...(prev.variant_stock || {}),
        [vkey]: {
          ...((prev.variant_stock || {})[vkey] || {}),
          [regionCode]: value === '' ? '' : Math.max(0, parseInt(value, 10) || 0),
        },
      },
      _variant_stock_pending: {
        ...(prev._variant_stock_pending || {}),
        [`${vkey}::${regionCode}`]: value === '' ? null : Math.max(0, parseInt(value, 10) || 0),
      },
    }));
  };

  // Region rows we surface in the variant stock matrix (super-admin sees all
  // selected regions; regional admin sees only their own).
  const stockRegionRows = isSuper
    ? (formData.regions || ['dmv', 'nj'])
    : editableRegions;

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
      <DropPushPreview target={pushPreview} onDone={() => { setPushPreview(null); onSuccess(); }} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-[#6B8E73] font-mono text-xs uppercase">Name *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
            required
            data-testid="product-name"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[#6B8E73] font-mono text-xs uppercase">Category *</Label>
          <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
            <SelectTrigger className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none" data-testid="product-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none">
              <SelectItem value="flower" className="text-[#39FF14] font-mono">FLOWER</SelectItem>
              <SelectItem value="preroll" className="text-[#39FF14] font-mono">PREROLL</SelectItem>
              <SelectItem value="edibles" className="text-[#39FF14] font-mono">EDIBLES</SelectItem>
              <SelectItem value="concentrates" className="text-[#39FF14] font-mono">CONCENTRATES</SelectItem>
              <SelectItem value="merch" className="text-[#39FF14] font-mono">MERCH</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Label className="text-[#6B8E73] font-mono text-xs uppercase">Description *</Label>
          {/* Phase 6.0 Sub-Phase 2 — LLM auto-fill for the long description.
              Works in both ADD (uses form data) and EDIT (uses saved product). */}
          {isSuper && (
            <Button
              type="button"
              size="sm"
              onClick={handleAutoFillDescription}
              disabled={autoFillingDesc}
              className="bg-[#B400FF] text-white hover:bg-[#C42BFF] rounded-none font-mono text-[10px] uppercase tracking-wider"
              data-testid="auto-fill-description-btn"
            >
              <Sparkles className={`w-3 h-3 mr-1 ${autoFillingDesc ? 'animate-pulse' : ''}`} />
              {autoFillingDesc ? 'DRAFTING…' : 'AUTO-FILL'}
            </Button>
          )}
        </div>
        <Textarea
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none resize-none"
          rows={2}
          required
          data-testid="product-description"
        />
        <p className="text-[#4B6B52] font-mono text-[10px] uppercase">// Plain factual notes. Used as raw material by the AI vibe writer.</p>
      </div>

      {/* Vibe description — available for all categories. Auto-generated on create
          UNLESS the operator opts out via the "Skip AI" checkbox; re-rollable on edit. */}
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-[#6B8E73] font-mono text-xs uppercase">Vibe Description (AI)</Label>
            {/* Feb 2026 — Skip AI checkbox is visible on BOTH create AND edit.
                On edit it acts as a "lock" — the operator can toggle it whenever
                they want to prevent future auto-generation, or the RE-ROLL button
                from accidentally rewriting their hand-tuned copy. */}
            <label className="flex items-center gap-2 text-[#A3C7A8] font-mono text-[10px] uppercase cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!formData.skip_ai_vibe}
                onChange={(e) => setFormData(prev => ({ ...prev, skip_ai_vibe: e.target.checked }))}
                className="accent-[#39FF14] w-3.5 h-3.5"
                data-testid="skip-ai-vibe-checkbox"
              />
              Skip AI · lock this text
            </label>
          </div>
          {internalProduct?.id ? (
            <div className="flex items-center gap-2">
              <Select value={vibeTone} onValueChange={setVibeTone} disabled={!!formData.skip_ai_vibe}>
                <SelectTrigger className="h-7 w-32 bg-[#050003] border-[#2A0F3D] text-[#A3C7A8] font-mono rounded-none text-[10px] uppercase disabled:opacity-40" data-testid="vibe-tone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none">
                  <SelectItem value="plain"     className="text-[#39FF14] font-mono text-xs">PLAIN ✓ DEFAULT</SelectItem>
                  <SelectItem value="random"    className="text-[#39FF14] font-mono text-xs">SURPRISE ME ✨</SelectItem>
                  <SelectItem value="casual"    className="text-[#39FF14] font-mono text-xs">CASUAL</SelectItem>
                  <SelectItem value="punchy"    className="text-[#39FF14] font-mono text-xs">PUNCHY</SelectItem>
                  <SelectItem value="poetic"    className="text-[#39FF14] font-mono text-xs">POETIC</SelectItem>
                  <SelectItem value="hype"      className="text-[#39FF14] font-mono text-xs">HYPE</SelectItem>
                  <SelectItem value="sommelier" className="text-[#39FF14] font-mono text-xs">SOMMELIER</SelectItem>
                  <SelectItem value="chill"     className="text-[#39FF14] font-mono text-xs">CHILL</SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="button"
                size="sm"
                onClick={handleRegenerateVibe}
                disabled={generatingVibe || !!formData.skip_ai_vibe}
                className="bg-[#B400FF] text-white hover:bg-[#C42BFF] rounded-none font-mono text-[10px] uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="regenerate-vibe-btn"
                title={formData.skip_ai_vibe ? 'Uncheck "Skip AI" to re-roll' : ''}
              >
                <Sparkles className={`w-3 h-3 mr-1 ${generatingVibe ? 'animate-pulse' : ''}`} />
                {generatingVibe ? 'WRITING…' : 'RE-ROLL'}
              </Button>
            </div>
          ) : null}
        </div>
        <Textarea
          value={formData.vibe_description}
          onChange={(e) => setFormData(prev => ({ ...prev, vibe_description: e.target.value }))}
          placeholder={
            formData.skip_ai_vibe
              ? 'AI is off — type the vibe description here (or leave blank).'
              : (internalProduct?.id
                  ? 'Pick a tone ↑ and tap RE-ROLL, or write your own.'
                  : 'Leave blank — Claude writes a plain 2-line description for you when you SAVE.')
          }
          className="bg-[#050003] border-[#2A0F3D] text-[#A3C7A8] font-mono rounded-none resize-none placeholder:text-[#4B6B52]"
          rows={3}
          data-testid="product-vibe-description"
        />
      </div>

      {/* Per-region The Drop. Saved products: instant per-region switches (dispatcher = own region).
          New products: one switch applied to the product's regions on create. */}
      {internalProduct?.id ? (
        <DropSection subtitle="// Saves instantly. Turning ON puts this product at the top of that region's Drop and may push customers."
          action={<DropSettingsDialog regions={isSuper ? (internalProduct.regions?.length ? internalProduct.regions : regions.map((r) => r.code)) : [actorRegion].filter(Boolean)} />}>
          <DropToggles
            key={internalProduct.id}
            productId={internalProduct.id}
            regions={isSuper ? (internalProduct.regions?.length ? internalProduct.regions : regions.map((r) => r.code)) : [actorRegion].filter(Boolean)}
            initial={internalProduct.region_drop}
            onChange={(rd) => { setInternalProduct((prev) => ({ ...prev, region_drop: rd })); onPhotoChange?.(); }}
          />
        </DropSection>
      ) : (
        <DropSection subtitle={isSuper ? "// Turns on The Drop in every region selected above, at the top." : `// Turns on The Drop for ${(actorRegion || '').toUpperCase()}, at the top.`}>
          <div className="flex justify-end">
            <DropSwitch on={!!formData.featured} onClick={() => setFormData((prev) => ({ ...prev, featured: !prev.featured }))} testId="featured-toggle" />
          </div>
        </DropSection>
      )}

      {/* Phase 6.0 Sub-Phase 2 — Lab Data editor (super-admin only).
          Cannabinoids + Terpenes are rendered as info panels in the customer
          ProductDetailDrawer. Backward compatible — empty values are stripped
          on save, and the drawer hides the panels when missing. */}
      {isSuper && (
        <details
          className="bg-[#0C0612] border border-[#39FF14]/30 p-0 rounded-none"
          data-testid="lab-data-accordion"
        >
          <summary className="cursor-pointer p-3 flex items-center justify-between gap-3 list-none">
            <div>
              <Label className="text-[#39FF14] font-mono text-xs uppercase tracking-wider cursor-pointer">
                🧪 LAB DATA (CANNABINOIDS + TERPENES)
              </Label>
              <p className="text-[#6B8E73] font-mono text-[10px] mt-1">
                // Renders as info panels on the customer product drawer.
                {Object.keys(formData.lab_data?.cannabinoids || {}).length === 0
                && Object.keys(formData.lab_data?.terpenes || {}).length === 0
                  ? ' Empty — tap to add or auto-fill.' : ''}
              </p>
            </div>
            {/* Auto-fill works in both ADD + EDIT mode (uses draft endpoint
                when there's no saved product yet). */}
            {(
              <Button
                type="button"
                size="sm"
                onClick={(e) => { e.preventDefault(); handleAutoFillLabData(); }}
                disabled={autoFillingLab}
                className="bg-[#B400FF] text-white hover:bg-[#C42BFF] rounded-none font-mono text-[10px] uppercase tracking-wider"
                data-testid="auto-fill-lab-data-btn"
              >
                <Sparkles className={`w-3 h-3 mr-1 ${autoFillingLab ? 'animate-pulse' : ''}`} />
                {autoFillingLab ? 'DRAFTING…' : 'AUTO-FILL'}
              </Button>
            )}
          </summary>

          <div className="border-t border-[#2A0F3D] p-3 space-y-4">
            <LabSection
              title="Cannabinoids"
              kind="cannabinoids"
              entries={formData.lab_data?.cannabinoids || {}}
              defaultKeyHint="THCA, THC-D9, CBGA, CBG, CBDA, CBD, CBC"
              onUpdate={updateLabValue}
              onRemove={removeLabEntry}
              onAdd={addLabEntry}
            />
            <LabSection
              title="Terpenes"
              kind="terpenes"
              entries={formData.lab_data?.terpenes || {}}
              defaultKeyHint="total, Beta Caryophyllene, Beta Myrcene, Limonene, Linalool, Humulene, Beta Pinene"
              onUpdate={updateLabValue}
              onRemove={removeLabEntry}
              onAdd={addLabEntry}
            />
          </div>
        </details>
      )}

      {/* Phase 5.9.12 (Feb 2026) — DEFAULT STOCK is auto-recomputed from
          per-region stock on every save, so showing the input was misleading
          (operators thought it was the source of truth). We now only render it
          for true legacy products: those with no region_stock map yet AND not
          a flower (flower already has per-region inputs below). For everything
          else, PRICE spans the full width.
          Phase 5.9.26 — for flower (uses ladder) and concentrate (uses per-
          variant prices) the top-level PRICE field is hidden entirely. The
          backend auto-sets it: flower → 0, concentrate → min variant price. */}
      {!hidePriceField && (() => {
        const hasRegionStock = product?.region_stock
          && Object.keys(product.region_stock).length > 0;
        const isLegacy = internalProduct?.id && !hasRegionStock && !isFlower;
        return (
          <div className={isLegacy ? 'grid grid-cols-2 gap-4' : ''}>
            <div className="space-y-2">
              <Label className="text-[#6B8E73] font-mono text-xs uppercase">
                Price *
              </Label>
              <Input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                required
                data-testid="product-price"
              />
            </div>
            {isLegacy && (
              <div className="space-y-2">
                <Label className="text-[#6B8E73] font-mono text-xs uppercase">
                  Default Stock <span className="text-[#FF9F0A]">(LEGACY)</span>
                </Label>
                <Input
                  type="number"
                  value={formData.stock}
                  onChange={(e) => setFormData(prev => ({ ...prev, stock: e.target.value }))}
                  className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                  data-testid="product-stock"
                />
                <p className="text-[#4B6B52] font-mono text-[10px]">
                  // This product predates per-region stock. Set per-region values below to retire this field.
                </p>
              </div>
            )}
          </div>
        );
      })()}

      {/* Phase 5.9.7 — per-region STOCK editor. The single legacy stock input
          above was confusing operators (it showed the same number regardless
          of region tab). This block lets you set the actual sellable count
          per region. Regional dispatchers only see their own row.

          Phase 6.0 — Hidden when the product has variants (concentrates,
          preroll, apparel). For variant products, the PER-REGION STOCK PER
          VARIANT matrix below is the single source of truth — duplicating
          per-region stock at the top level was creating two sources of truth
          and confusing operators. Only show top-level region stock for
          flower + edibles + merch (non-variant). */}
      {!variantSupport && (
      <div className="space-y-2 p-3 bg-[#0C0612] border border-[#39FF14]/30" data-testid="per-region-stock-block">
        <Label className="text-[#39FF14] font-mono text-xs uppercase">
          // Per-Region Stock {isFlower ? '(eighths)' : ''}
        </Label>
        <p className="text-[#4B6B52] font-mono text-[10px]">
          What's physically sellable in each region. Customers in DMV can't buy NJ stock and vice versa.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
          {(isSuper ? (formData.regions || ['dmv', 'nj']) : editableRegions).map((region) => {
            const cur = (product?.region_stock || {})[region] ?? '';
            return (
              <div key={region} className="flex items-center gap-2">
                <span className="text-[#00F0FF] font-mono text-xs uppercase w-12 shrink-0">{region}</span>
                <Input
                  type="number"
                  min="0"
                  defaultValue={cur}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      _region_stock_pending: {
                        ...(prev._region_stock_pending || {}),
                        [region]: v === '' ? null : parseInt(v, 10),
                      },
                    }));
                  }}
                  className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none"
                  placeholder="0"
                  data-testid={`region-stock-input-${region}`}
                />
              </div>
            );
          })}
        </div>
        <p className="text-[#4B6B52] font-mono text-[9px]">
          // Tip: leave blank to keep current value. Use the table-row inline input for quick edits.
        </p>
      </div>
      )}

      {/* Phase 5.9.5 — super-admin "Available regions" multi-select. Lets the
          founder mark a single product as available in multiple regions
          (e.g. ["dmv","nj"]) instead of needing one product per region.
          Regional dispatchers don't see this — their region is auto-tagged
          on create and the field is immutable from their side. */}
      {isSuper && (
        <div className="space-y-2 p-3 bg-[#0C0612] border border-[#00F0FF]/30" data-testid="product-regions-block">
          <Label className="text-[#00F0FF] font-mono text-xs uppercase">
            // Available Regions
          </Label>
          <p className="text-[#4B6B52] font-mono text-[10px]">
            Which regions can buy this product? Customers are invisible to region;
            this just controls catalog visibility per region.
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            {regions.map((r) => {
              const active = (formData.regions || []).includes(r.code);
              return (
                <button
                  key={r.code}
                  type="button"
                  onClick={() => {
                    setFormData((prev) => {
                      const current = new Set(prev.regions || []);
                      if (current.has(r.code)) {
                        current.delete(r.code);
                      } else {
                        current.add(r.code);
                      }
                      return { ...prev, regions: Array.from(current) };
                    });
                  }}
                  data-testid={`product-region-toggle-${r.code}`}
                  className={`px-3 py-1.5 border font-mono text-xs uppercase tracking-wider transition-all ${
                    active
                      ? 'border-[#00F0FF] text-[#00F0FF] bg-[rgba(0,240,255,0.08)]'
                      : 'border-[#2A0F3D] text-[#4B6B52] hover:border-[#4B6B52] hover:text-[#A3C7A8]'
                  }`}
                >
                  {active ? '✓ ' : ''}{r.code.toUpperCase()}
                </button>
              );
            })}
          </div>
          {(formData.regions || []).length === 0 && (
            <p className="text-[#FF3D8E] font-mono text-[11px] font-bold pt-1" data-testid="product-regions-empty-warning">
              ⚠ Pick at least one region — required. Products no longer auto-publish to all regions.
            </p>
          )}
        </div>
      )}

      {/* Phase 5.9.7 — merch subcategory picker. Only shown when category=merch.
          Driving variants-editor for "apparel" too. */}
      {formData.category === 'merch' && (
        <div className="space-y-2">
          <Label className="text-[#6B8E73] font-mono text-xs uppercase">Merch Sub-Category</Label>
          <Select
            value={formData.subcategory || ''}
            onValueChange={(v) => setFormData(prev => ({ ...prev, subcategory: v }))}
          >
            <SelectTrigger className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none" data-testid="product-subcategory">
              <SelectValue placeholder="Select sub-category..." />
            </SelectTrigger>
            <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none">
              {['apparel', 'glassware', 'grinders', 'papers', 'lighters', 'hats', 'stickers', 'vape_carts', 'other'].map((s) => (
                <SelectItem key={s} value={s} className="text-[#39FF14] font-mono">{s.toUpperCase()}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Phase 2 — Variants editor (apparel sizes + concentrate weights). */}
      {variantSupport && (
        <div className="space-y-3 p-3 bg-[#0C0612] border border-[#00F0FF]/40" data-testid="variants-editor">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <Label className="text-[#00F0FF] font-mono text-xs uppercase">
              // {formData.category === 'concentrates' ? 'Concentrate Weights' : formData.category === 'preroll' ? 'Preroll Pack Sizes' : 'Apparel Sizes'}
            </Label>
            <div className="flex items-center gap-1.5">
              {isSuper && !hasAnyVariants && (
                <Button
                  type="button"
                  size="sm"
                  onClick={seedVariantsFromTemplate}
                  className="bg-[#00F0FF] text-[#050003] hover:bg-[#33F5FF] rounded-none font-mono text-[10px] uppercase tracking-wider"
                  data-testid="load-default-variants-btn"
                >
                  <Sparkles className="w-3 h-3 mr-1" />
                  LOAD DEFAULTS
                </Button>
              )}
              {isSuper && hasAnyVariants && (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addBlankVariant}
                    className="border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 rounded-none font-mono text-[10px] uppercase tracking-wider"
                    data-testid="add-variant-btn"
                  >
                    <Plus className="w-3 h-3 mr-1" /> ADD ROW
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={clearVariants}
                    className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-none font-mono text-[10px] uppercase tracking-wider"
                    data-testid="clear-variants-btn"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> CLEAR
                  </Button>
                </>
              )}
            </div>
          </div>

          {!hasAnyVariants && (
            <p className="text-[#4B6B52] font-mono text-[11px]">
              {isSuper
                ? `Tap LOAD DEFAULTS to seed standard ${formData.category === 'concentrates' ? '0.5g / 1g / 2g' : formData.category === 'preroll' ? '1g / 3.5g / 5g' : 'XS / S / M / L / XL / XXL'} rows, then set per-region stock${wantsVariantPriceColumn ? ' and price' : ''} below.`
                : '// No variants defined yet. Ask super-admin to set up sizes.'}
            </p>
          )}

          {hasAnyVariants && (
            <div className="space-y-2">
              <div className={`hidden sm:grid gap-2 text-[#4B6B52] font-mono text-[10px] uppercase tracking-wider ${wantsVariantPriceColumn ? 'grid-cols-[1fr_1fr_1fr_auto]' : 'grid-cols-[1fr_1fr_auto]'}`}>
                <span>Key</span>
                <span>Label</span>
                {wantsVariantPriceColumn && <span>Price ($)</span>}
                <span className="sr-only">Actions</span>
              </div>
              {(formData.variants || []).map((v, idx) => {
                const keyError = isSuper && v.key !== '' && v.key !== undefined ? validateVariantKey(v.key) : null;
                return (
                <div
                  key={`${v.key || 'new'}-${idx}`}
                  className={`grid gap-2 items-start ${wantsVariantPriceColumn ? 'grid-cols-[1fr_1fr_1fr_auto]' : 'grid-cols-[1fr_1fr_auto]'}`}
                  data-testid={`variant-row-${idx}`}
                >
                  <div className="flex flex-col">
                    <Input
                      value={v.key || ''}
                      onChange={(e) => updateVariantField(idx, 'key', e.target.value)}
                      placeholder={formData.category === 'concentrates' ? '0.5g' : formData.category === 'preroll' ? '1g' : 'M'}
                      disabled={!isSuper}
                      className={`bg-[#050003] font-mono rounded-none text-xs ${
                        keyError
                          ? 'border-[#FF3B30] text-[#FF3B30] focus-visible:ring-[#FF3B30]'
                          : 'border-[#2A0F3D] text-[#39FF14]'
                      }`}
                      data-testid={`variant-key-${idx}`}
                      aria-invalid={!!keyError}
                    />
                    {keyError && (
                      <span
                        className="text-[#FF3B30] text-[10px] font-mono mt-1 leading-tight"
                        data-testid={`variant-key-error-${idx}`}
                      >
                        ⚠ {keyError}
                      </span>
                    )}
                  </div>
                  <Input
                    value={v.label || ''}
                    onChange={(e) => updateVariantField(idx, 'label', e.target.value)}
                    placeholder={formData.category === 'concentrates' ? '0.5g' : formData.category === 'preroll' ? '1g' : 'Medium'}
                    disabled={!isSuper}
                    className="bg-[#050003] border-[#2A0F3D] text-[#A3C7A8] font-mono rounded-none text-xs"
                    data-testid={`variant-label-${idx}`}
                  />
                  {wantsVariantPriceColumn && (() => {
                    // Phase 5.9.26 — concentrate variants MUST have a positive
                    // price (each weight is priced differently). Show inline
                    // error when missing or zero. Mirrors backend validator.
                    const rawPrice = v.price;
                    const priceNum = rawPrice === '' || rawPrice === undefined || rawPrice === null
                      ? null
                      : Number(rawPrice);
                    const priceError = isConcentrate && isSuper && (priceNum === null || !Number.isFinite(priceNum) || priceNum <= 0)
                      ? 'Required (each variant is priced differently)'
                      : null;
                    return (
                      <div className="flex flex-col">
                        <Input
                          type="number"
                          step="0.01"
                          value={rawPrice === undefined || rawPrice === null ? '' : rawPrice}
                          onChange={(e) => updateVariantField(idx, 'price', e.target.value)}
                          placeholder={formData.category === 'preroll' ? '15.00' : '25.00'}
                          disabled={!isSuper}
                          className={`bg-[#050003] font-mono rounded-none text-xs ${
                            priceError
                              ? 'border-[#FF3B30] text-[#FF3B30] focus-visible:ring-[#FF3B30]'
                              : 'border-[#2A0F3D] text-[#39FF14]'
                          }`}
                          aria-invalid={!!priceError}
                          data-testid={`variant-price-${idx}`}
                        />
                        {priceError && (
                          <span
                            className="text-[#FF3B30] text-[10px] font-mono mt-1 leading-tight"
                            data-testid={`variant-price-error-${idx}`}
                          >
                            ⚠ {priceError}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  {isSuper ? (
                    <button
                      type="button"
                      onClick={() => removeVariant(idx)}
                      className="text-[#FF3B30] hover:bg-[#FF3B30]/10 p-1 border border-transparent hover:border-[#FF3B30] self-start"
                      title="Remove this variant"
                      data-testid={`variant-remove-${idx}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <span className="text-[#4B6B52]">&nbsp;</span>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {/* Per-variant per-region stock matrix */}
          {hasAnyVariants && stockRegionRows.length > 0 && (
            <div className="pt-2 border-t border-[#00F0FF]/30 space-y-2">
              <p className="text-[#00F0FF] font-mono text-[11px] uppercase tracking-wider">
                // Per-Region Stock per Variant
              </p>
              {(formData.variants || []).filter((v) => v.key).map((v) => (
                <div key={v.key} className="grid grid-cols-1 sm:grid-cols-[100px_1fr] gap-2 items-center">
                  <span className="text-[#A3C7A8] font-mono text-xs uppercase">{v.label || v.key}</span>
                  <div className="grid grid-cols-2 gap-2">
                    {stockRegionRows.map((r) => {
                      const cur = (formData.variant_stock?.[v.key] || {})[r];
                      return (
                        <div key={r} className="flex items-center gap-2">
                          <span className="text-[#00F0FF] font-mono text-[10px] uppercase w-8 shrink-0">{r}</span>
                          <Input
                            type="number"
                            min="0"
                            value={cur === undefined || cur === null ? '' : cur}
                            onChange={(e) => setVariantStock(v.key, r, e.target.value)}
                            placeholder="0"
                            className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none text-xs"
                            data-testid={`variant-stock-${v.key}-${r}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              <p className="text-[#4B6B52] font-mono text-[10px]">
                // Stock saves on UPDATE. Customers see "out of stock" per-variant once a slot hits 0.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Phase 5.9.4 — per-region flower price ladders. Regional admins see
          ONLY their own region's ladder; super admin sees one panel per region. */}
      {isFlower && editableRegions.map((region) => (
        <div key={region} className="space-y-2 p-3 bg-[#0C0612] border border-[#B400FF]/30">
          <div className="flex items-center justify-between">
            <Label className="text-[#B400FF] font-mono text-xs uppercase">
              // {region.toUpperCase()} Flower Price Ladder
            </Label>
            <Badge className="bg-[#B400FF]/20 text-[#B400FF] border-0 rounded-none font-mono text-[10px] uppercase">
              {region}
            </Badge>
          </div>
          <p className="text-[#4B6B52] font-mono text-[10px] mb-2">
            Set prices for {region.toUpperCase()} customers. Blank = use the default ladder ($30 / $45 / $70 / $130).
          </p>
          <div className="grid grid-cols-4 gap-2">
            {[
              { key: '3.5g', label: '3.5g',   placeholder: '30.00' },
              { key: '7g',   label: '7g',     placeholder: '45.00' },
              { key: '14g',  label: '1/2 oz', placeholder: '70.00' },
              { key: '28g',  label: '1 oz',   placeholder: '130.00' },
            ].map((s) => (
              <div key={s.key} className="space-y-1">
                <Label className="text-[#6B8E73] font-mono text-[10px]">{s.label}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.region_prices[region]?.[s.key] ?? ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      region_prices: {
                        ...prev.region_prices,
                        [region]: {
                          ...(prev.region_prices[region] || {}),
                          [s.key]: e.target.value,
                        },
                      },
                    }))
                  }
                  className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none text-xs"
                  placeholder={s.placeholder}
                  data-testid={`product-flower-price-${region}-${s.key}`}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Feb 2026 — Per-region variant price ladders for concentrate/preroll.
          Mirrors the flower ladder architecture so regional dispatchers can
          independently price non-flower variants (e.g. NJ prices vape 2g
          differently than DMV). Backend `_variant_price_for` already prefers
          `region_prices.<region>.variant_prices.<key>` over the shared
          `variants[].price`. The shared row above is now the fallback/default. */}
      {wantsVariantPriceColumn && hasAnyVariants && editableRegions.map((region) => (
        <div key={`vp-${region}`} className="space-y-2 p-3 bg-[#0C0612] border border-[#B400FF]/30" data-testid={`region-variant-price-panel-${region}`}>
          <div className="flex items-center justify-between">
            <Label className="text-[#B400FF] font-mono text-xs uppercase">
              // {region.toUpperCase()} {formData.category === 'concentrates' ? 'Concentrate' : 'Preroll'} Price Ladder
            </Label>
            <Badge className="bg-[#B400FF]/20 text-[#B400FF] border-0 rounded-none font-mono text-[10px] uppercase">
              {region}
            </Badge>
          </div>
          <p className="text-[#4B6B52] font-mono text-[10px] mb-2">
            {`Set ${region.toUpperCase()} prices per size. Blank = fall back to the shared price above.`}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {(formData.variants || []).filter((v) => v.key).map((v) => {
              const sharedPrice = v.price === '' || v.price === undefined || v.price === null
                ? null
                : Number(v.price);
              const placeholder = sharedPrice && Number.isFinite(sharedPrice) && sharedPrice > 0
                ? sharedPrice.toFixed(2)
                : '0.00';
              const cur = formData.region_variant_prices?.[region]?.[v.key] ?? '';
              return (
                <div key={v.key} className="space-y-1">
                  <Label className="text-[#6B8E73] font-mono text-[10px]">{v.label || v.key}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={cur}
                    onChange={(ev) =>
                      setFormData((prev) => ({
                        ...prev,
                        region_variant_prices: {
                          ...prev.region_variant_prices,
                          [region]: {
                            ...(prev.region_variant_prices?.[region] || {}),
                            [v.key]: ev.target.value,
                          },
                        },
                      }))
                    }
                    className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none text-xs"
                    placeholder={placeholder}
                    data-testid={`product-variant-price-${region}-${v.key}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Strain Type — flower only */}
      {isFlower && (
        <div className="space-y-2">
          <Label className="text-[#6B8E73] font-mono text-xs uppercase">Strain Type</Label>
          <Select value={formData.strain_type} onValueChange={(v) => setFormData(prev => ({ ...prev, strain_type: v }))}>
            <SelectTrigger className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none" data-testid="product-strain">
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent className="bg-[#0C0612] border-[#2A0F3D] rounded-none">
              <SelectItem value="indica" className="text-[#39FF14] font-mono">INDICA</SelectItem>
              <SelectItem value="sativa" className="text-[#39FF14] font-mono">SATIVA</SelectItem>
              <SelectItem value="hybrid" className="text-[#39FF14] font-mono">HYBRID</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Phase 5.9 — AI generation + dispatcher upload override.
          Existing products get both actions; new products show the URL field
          only (image actions need an id to attach to). */}
      <div className="space-y-3">
        <Label className="text-[#6B8E73] font-mono text-xs uppercase">Product Photo</Label>

        {internalProduct?.id && (
          <>
            {/* AI direction — optional free-text steer for the model.
                Empty = use category default. Filled = appended to brand prompt. */}
            <Input
              value={imagePrompt}
              onChange={(e) => setImagePrompt(e.target.value)}
              placeholder="AI direction — what to change for RESTYLE / FROM PHOTO (e.g. 'sunset beach mood, warm film look'). Empty = better background + lighting only"
              className="bg-[#050003] border-[#2A0F3D] text-[#A3C7A8] font-mono rounded-none text-xs placeholder:text-[#4B6B52]"
              data-testid="image-prompt-input"
              disabled={generatingImage || uploadingImage}
            />

            <div className="flex items-center gap-2 flex-wrap">
              <Button
                type="button"
                size="sm"
                onClick={() => handleGenerateImage({ useReference: false })}
                disabled={generatingImage || uploadingImage}
                className="bg-[#B400FF] text-white hover:bg-[#C42BFF] rounded-none font-mono text-[10px] uppercase tracking-wider"
                data-testid="generate-image-btn"
                title="Generate a brand-new image from text"
              >
                <Sparkles className={`w-3 h-3 mr-1 ${generatingImage ? 'animate-pulse' : ''}`} />
                {generatingImage ? 'GENERATING…' : 'AI GENERATE'}
              </Button>

              {formData.image_url && (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => handleGenerateImage({ useReference: true })}
                  disabled={generatingImage || uploadingImage}
                  variant="outline"
                  className="border-[#B400FF] text-[#B400FF] hover:bg-[#B400FF]/10 rounded-none font-mono text-[10px] uppercase tracking-wider"
                  data-testid="restyle-image-btn"
                  title="Reuse current photo as reference and apply your direction"
                >
                  <Sparkles className={`w-3 h-3 mr-1 ${generatingImage ? 'animate-pulse' : ''}`} />
                  AI RESTYLE
                </Button>
              )}

              {/* Phase 5.9.10 — generate from a user-supplied reference photo
                  (vendor shot, your own product photo, etc.). Use this when the
                  text-only AI GENERATE doesn't quite match the real product. */}
              <label className="inline-flex">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleGenerateFromPhoto}
                  disabled={generatingImage || uploadingImage}
                  className="hidden"
                  data-testid="generate-from-photo-input"
                />
                <span
                  role="button"
                  tabIndex={0}
                  aria-disabled={generatingImage || uploadingImage}
                  className={`inline-flex items-center gap-1 px-3 h-8 border border-[#FFCC00] text-[#FFCC00] hover:bg-[#FFCC00]/10 rounded-none font-mono text-[10px] uppercase tracking-wider cursor-pointer ${
                    (generatingImage || uploadingImage) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  data-testid="generate-from-photo-btn"
                  title="Pick a reference photo from your device — AI will generate a brand-aligned version of it"
                >
                  <Camera className={`w-3 h-3 mr-1 ${generatingImage ? 'animate-pulse' : ''}`} />
                  AI FROM PHOTO
                </span>
              </label>

              <label className="inline-flex">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadImage}
                  disabled={generatingImage || uploadingImage}
                  className="hidden"
                  data-testid="upload-image-input"
                />
                <span
                  role="button"
                  tabIndex={0}
                  aria-disabled={generatingImage || uploadingImage}
                  className={`inline-flex items-center gap-1 px-3 h-8 border border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10 rounded-none font-mono text-[10px] uppercase tracking-wider cursor-pointer ${
                    (generatingImage || uploadingImage) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  data-testid="upload-image-btn"
                >
                  <Upload className={`w-3 h-3 ${uploadingImage ? 'animate-pulse' : ''}`} />
                  {uploadingImage ? 'UPLOADING…' : 'UPLOAD'}
                </span>
              </label>

              {/* Phase 5.9.10 hotfix — explicit DELETE PHOTO so a bad AI render
                  can be reverted without first uploading a replacement. */}
              {formData.image_url && (
                <Button
                  type="button"
                  size="sm"
                  onClick={handleDeleteImage}
                  disabled={generatingImage || uploadingImage}
                  variant="outline"
                  className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-none font-mono text-[10px] uppercase tracking-wider"
                  data-testid="delete-image-btn"
                  title="Remove this photo entirely"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  DELETE PHOTO
                </Button>
              )}
            </div>
          </>
        )}

        {/* Preview */}
        <div className="flex items-start gap-3">
          <div
            className="w-24 h-24 flex-shrink-0 bg-[#050003] border border-[#2A0F3D] flex items-center justify-center overflow-hidden"
            data-testid="product-image-preview"
          >
            {previewSrc ? (
              <img
                src={previewSrc}
                alt="Product"
                className="w-full h-full object-cover"
              />
            ) : (
              <ImageIcon className="w-8 h-8 text-[#4B6B52]" />
            )}
          </div>
          <div className="flex-1 space-y-1">
            <Input
              value={formData.image_url}
              onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
              className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none text-xs"
              placeholder="https://… or use AI / Upload →"
              data-testid="product-image"
            />
            <p className="text-[#4B6B52] font-mono text-[10px] uppercase">
              {internalProduct?.id
                ? '// AI generates a brand-aligned shot. Upload overrides with your own.'
                : '// Save first to enable AI generation and upload.'}
            </p>
          </div>
        </div>
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-[#39FF14] text-[#050003] font-bold hover:bg-[#4AF626] rounded-none h-12"
        data-testid="save-product"
      >
        {loading ? 'SAVING...' : (internalProduct ? 'UPDATE PRODUCT' : 'CREATE PRODUCT')}
      </Button>
      {/* Phase 6.0 — VIEW AS CUSTOMER ↗ ghost link. Only appears once the
          product exists (post-create or in edit mode). Opens the customer
          dashboard with `?open={id}&preview=1` so the operator can verify
          their edits inside the SAME PWA context (preserving auth cookies
          — `target="_blank"` would launch external Safari with no cookies
          and bounce to login on iOS). */}
      {internalProduct?.id && (
        <Link
          to={`/customer?open=${internalProduct.id}&preview=1`}
          className="block text-center text-[#7FD8FF] font-mono text-[10px] uppercase tracking-wider mt-2 py-1 border border-[#7FD8FF]/30 hover:border-[#7FD8FF] transition-colors rounded-none"
          data-testid="view-as-customer-link"
        >
          VIEW AS CUSTOMER →
        </Link>
      )}
    </form>
  );
}

// Phase 6.0 Sub-Phase 2 — Lab data sub-section (Cannabinoids or Terpenes).
// Renders existing key/value rows + a [+ ADD] row for new entries. Empty
// values are stripped on save by the form's submit handler.
function LabSection({ title, kind, entries, defaultKeyHint, onUpdate, onRemove, onAdd }) {
  const [newKey, setNewKey] = useState('');
  const keys = Object.keys(entries);
  return (
    <div className="space-y-2" data-testid={`lab-section-${kind}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label className="text-[#A3C7A8] font-mono text-xs uppercase tracking-wider">
          {title}
        </Label>
        <span className="text-[#4B6B52] font-mono text-[10px]">
          // {keys.length} entr{keys.length === 1 ? 'y' : 'ies'}
        </span>
      </div>
      <div className="space-y-1">
        {keys.length === 0 && (
          <p className="text-[#4B6B52] font-mono text-[10px]">
            // No entries yet. Suggested keys: {defaultKeyHint}
          </p>
        )}
        {keys.map((k) => (
          <div key={k} className="flex items-center gap-2" data-testid={`lab-row-${kind}-${k}`}>
            <Input
              value={k}
              disabled
              className="bg-[#050003] border-[#2A0F3D] text-[#A3C7A8] font-mono rounded-none flex-1 text-xs"
            />
            <div className="relative w-24">
              <Input
                type="number"
                step="0.01"
                min="0"
                value={entries[k] ?? ''}
                onChange={(e) => onUpdate(kind, k, e.target.value)}
                placeholder="0.0"
                className="bg-[#050003] border-[#2A0F3D] text-[#39FF14] font-mono rounded-none pr-6 text-xs"
                data-testid={`lab-input-${kind}-${k}`}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[#4B6B52] font-mono text-[10px]">%</span>
            </div>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => onRemove(kind, k)}
              className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-none font-mono text-[10px] px-2"
              data-testid={`lab-remove-${kind}-${k}`}
              aria-label={`Remove ${k}`}
            >
              ×
            </Button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Input
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          placeholder={`Add ${title.slice(0, -1).toLowerCase()} name (e.g., ${(defaultKeyHint.split(',')[0] || '').trim()})`}
          className="bg-[#050003] border-[#2A0F3D] text-[#A3C7A8] font-mono rounded-none flex-1 text-xs placeholder:text-[#4B6B52]"
          data-testid={`lab-new-key-${kind}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (newKey.trim()) { onAdd(kind, newKey); setNewKey(''); }
            }
          }}
        />
        <Button
          type="button"
          size="sm"
          onClick={() => {
            if (newKey.trim()) { onAdd(kind, newKey); setNewKey(''); }
          }}
          className="bg-[#2A0F3D] text-[#39FF14] hover:bg-[#39FF14]/10 rounded-none font-mono text-[10px] uppercase tracking-wider"
          data-testid={`lab-add-${kind}`}
        >
          + ADD
        </Button>
      </div>
    </div>
  );
}
